import type { FC } from 'react'
import React from 'react'

import type { HeadingProps } from './types'
import type { PrivateThemeUiProps } from '../../../styles-system/create-component'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { get } from '../../../utils/object'
import { TextPrivate } from '../text/text'

function isHeadingType(v?: string) {
  return v && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(v.toLowerCase())
}

export const HeadingPrivate = React.forwardRef<
  HTMLSpanElement,
  HeadingProps &
    PrivateThemeUiProps & {
      theme?: Theme
    }
>(function HeadingPrivate(props, ref) {
  const {
    as: asProps = 'h1',
    __css,
    __themeKey = 'headings',
    className = 'csg-tui-heading',
    variant = '__default',
    children,
    ellipsis,
    lhCrop: lhCropProps,
    ...rest
  } = props
  const theme = useSafeThemeUI(props)
  const lhCrop =
    typeof lhCropProps === 'boolean' && lhCropProps
      ? __themeKey !== 'headings' && variant
        ? // if themeKey is not default find possible lhCrop in the theme so we know how to handle the condition wrap
          get(
            theme,
            `${__themeKey}.${variant}.lhCrop`,
            isHeadingType(asProps) ? `heading.${asProps}` : 'body'
          )
        : isHeadingType(asProps)
          ? `heading.${asProps}`
          : 'body'
      : lhCropProps
  return (
    <TextPrivate
      __themeKey={__themeKey}
      ref={ref}
      as={asProps}
      variant={variant ? variant : asProps}
      className={className}
      lhCrop={lhCrop ? lhCrop : undefined}
      ellipsis={ellipsis}
      resetCss={{
        marginBlockStart: 'unset',
        marginBlockEnd: 'unset'
      }}
      {...rest}
    >
      {children}
    </TextPrivate>
  )
})

const Heading: FC<HeadingProps> = React.forwardRef<
  HTMLSpanElement,
  HeadingProps
>(function Heading(props, ref) {
  return <HeadingPrivate ref={ref} {...props} />
})

export default Heading
